/**
 * FP 플로우차트(다이어그램) 공통 함수 모듈.
 *
 * old 대응:
 *   - `wingui/view/factoryplan/common/utils/diagram.js`  → 트리 조립(§조립) + 툴팁 + 딥링크
 *   - `wingui-core/component/workflow/util/TidyTreeUtil.js` → 트리 평탄화/노드·엣지 변환(§변환)
 *   - `wingui-core/component/workflow/util/TreeToNodes.js`  → 좌표 배치(§배치)
 *
 * **dagre 미사용(자체 구현)**: old는 `dagre.layout()`으로 좌표만 얻어 썼다(`node.x`/`node.y`만 읽고
 * `edge.points`/그래프 bbox는 쓰지 않음, 노드 크기도 전 노드 동일 상수). 즉 dagre가 하던 일은
 * "동일 크기 박스들의 계층 좌표 계산" 하나뿐이라, 아래 `layoutFlow`가 그 범위만 재구현한다
 * (사이클 제거 → 랭킹 → 랭크 내 순서 → 좌표). 신규 의존성을 추가하지 않기 위한 결정
 * (2026-08-12 사용자 지시, `reports/pending-decisions.md` §3-1 참고).
 *
 * 이 파일은 순수 함수만 둔다. 렌더링은 `common/component/FlowChart.js`(래퍼) +
 * `common/component/FlowNode.js`(도형)가 담당한다.
 */
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";

// old `Flo.jsx:41-42`의 nodeWidth/nodeHeight 고정 상수. old는 dagre 기본
// nodesep/ranksep(50)을 그대로 썼으므로 같은 값을 쓴다.
export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 70;
const RANK_SEP = 50;
const NODE_SEP = 50;

// 좌우(LR) 기준: 랭크는 열, 랭크 내 정렬은 세로.
const RANK_GAP = NODE_WIDTH + RANK_SEP;
const NODE_GAP = NODE_HEIGHT + NODE_SEP;


/* ------------------------------------------------------------------ *
 * §조립 — 백엔드 FLO 트리(JSON) → 화면용 트리 노드
 *   old `diagram.js`의 getBasicNode/connectRoute/connectBor/connectByproduct/connectChildren 이식.
 *   트리 노드는 reactflow 노드가 아니라 순수 도메인 객체다(변환은 §변환에서).
 * ------------------------------------------------------------------ */

/** old getBasicNode — id는 `업무코드_명칭`. 같은 코드는 §변환에서 1개 노드로 병합된다. */
export const createNode = (nodeId, nodeNm, shape, tooltip, label) => ({
  id: `${nodeId}_${nodeNm}`,
  shape,
  tooltip: tooltip || "",
  label: label || nodeNm,
  children: []
});

/**
 * 연산품 표시 — `productType`과 함께 **id에 역할 접미사**를 붙인다.
 *
 * 같은 품목 코드라도 **생산품/투입 자재로 쓰인 것과 연산품으로 나온 것은 다른 사실**이라
 * 별개 도형으로 그려야 한다(2026-08-13 사용자 결정). id를 그대로 두면 §변환의 병합에서
 * 하나로 합쳐지면서 old 시절부터 있던 두 문제가 그대로 재현된다:
 *   - 주품목 X와 같은 공정의 연산품 X → **엣지 id까지 같아져 연산품 점선 엣지가 통째로 소실**
 *   - 같은 공정에 자재 M 투입 + 연산품 M 산출 → `M → 공정 → M` **가짜 순환**
 *
 * 접미사는 **연산품에만** 붙인다. 자원처럼 여러 공정이 공유하는 노드는 한 도형으로 묶여야
 * 하므로(사용자 확인) 병합 규칙 자체는 건드리지 않는다.
 */
const BYPRODUCT_ID_SUFFIX = "@BY";
const markByproduct = (node) => {
  node.id += BYPRODUCT_ID_SUFFIX;
  node.productType = "byproduct";
  return node;
};

/**
 * 툴팁 줄 조립 — **값이 없는(undefined/null) 줄은 아예 표시하지 않는다.**
 * 백엔드가 도형 종류에 따라 안 보내는 필드가 있어서(연산품엔 `bomRate`가 없다) 그대로 찍으면
 * `투입 비율 : undefined`가 화면에 노출된다(old도 같은 증상이었다). 빈 문자열("")은 값이 있는
 * 것으로 보고 그대로 표시한다 — old가 `부연 설명 : `를 빈칸으로 보여주던 것과 동일.
 */
const tooltipLines = (lines) => lines
  .filter(([, value]) => value !== undefined && value !== null)
  .map(([label, value]) => `${label} : ${value}`)
  .join("\n");

/**
 * 코드값 → 다국어 라벨. 키(`<그룹>_<코드>`)가 없으면 **코드를 그대로** 돌려준다(§0-7과 동일 규칙).
 * 툴팁이 `품목 구분 : I`처럼 코드 원문을 노출하던 것을 사람이 읽는 문구로 바꾸기 위한 것 —
 * old가 코드를 그대로 찍던 부분이라 파리티가 아니라 개선이다.
 */
const codeLabel = (t, groupKey, code) => {
  if (code === undefined || code === null || code === "") return code;
  const key = `${groupKey}_${code}`;
  const label = t(key);
  return label === key ? code : label;
};

export const makeInventoryTooltip = (data, t, isByproduct = false) => tooltipLines([
  [t("FP_INVENTORY_CD"), data.inventoryCd],
  [t("FP_INVENTORY_NAME"), data.inventoryNm],
  [t("FP_ITEM_CD"), data.itemCd],
  [t("FP_ITEM_NM"), data.itemNm],
  [t("FP_ITEM_CLASS_CD"), codeLabel(t, "FP_ITEM_CLASS_CD", data.itemClassCd)],
  // 연산품은 투입량(bomRate)이 아니라 산출 값을 갖는다 — 백엔드가 `ByProductDto.outputVal`을
  // `productRate` 키로 담아 보낸다(`MasterService#toMapBomRoutingDto`). 라벨은 old 연산품 마스터
  // 화면이 같은 컬럼에 쓰던 `FP_OUTPUT_VAL`("생산 값")을 그대로 쓴다(`Byproduct.jsx:72`).
  ...(isByproduct
    ? [
      [t("FP_OUTPUT_VAL"), data.productRate],
      // 생산 값이 절대 수량인지 주문수량 배율인지에 따라 숫자의 의미가 달라져 같이 보여준다.
      // ⚠️ 현재 FLO 응답엔 `outputValTpCd`가 담기지 않는다(backend-gaps.md #9) — 값이 없으면
      // `tooltipLines`가 이 줄을 통째로 생략하므로, 백엔드가 내려주기 시작하면 바로 표시된다.
      [t("FP_OUTPUT_VAL_TYPE"), codeLabel(t, "FP_OUTPUT_VAL_TP_CD", data.outputValTpCd)]
    ]
    : [[t("FP_BOM_RATE"), data.bomRate]]),
  [t("FP_ITEM_UOM"), data.itemUom]
]);

export const makeRouteTooltip = (data, t) =>
  `${t("FP_ROUTE_CD")} : ${data.routeCd}\n${t("FP_ROUTE_NM")} : ${data.routeNm}`;

// old는 자원코드 라벨로 전역 키 `RES_CD`를 썼으나 new엔 없다 → 동일 의미의 기존 FP 키를 재사용
// (fp-frontend.md §0-3 "의미상 동일한 키가 이미 있으면 재사용, 신규 생성 금지").
export const makeResourceTooltip = (data, t) => [
  `${t("FP_RESOURCE_CD")} : ${data.resourceCd}`,
  `${t("FP_RESOURCE_NM")} : ${data.resourceNm}`,
  `${t("FP_DESC_TXT")} : ${data.resourceDescTxt}`
].join("\n");

export const makeBorSetTooltip = (data, t) => `${t("FP_BOR_SET_CD")} : ${data.borSetCd}`;

/**
 * old `getLinkByType` 대응 — old는 `history.push({pathname, state})`였지만 new는 SPA 탭이라
 * URL 라우팅이 없다. 대신 딥링크 규약(fp-master-screen-guide.md §13)에 맞춰
 * "대상 화면 reduxKey + 그 화면에 심어줄 조회조건 필드"를 반환한다.
 * 필드명은 대상 화면의 SEARCH_FIELD_LABELS 키와 정확히 일치해야 한다.
 */
export const getNavByType = (type, data) => {
  switch (type) {
    case "resource":
      return { reduxKey: "FP_RESOURCE", fields: { planScope: data.planScope, resourceCdNm: data.resourceCd } };
    case "tool":
      return { reduxKey: "FP_RESOURCE", fields: { planScope: data.planScope, resourceCdNm: data.toolResourceCd } };
    case "borset":
      return { reduxKey: "FP_BOR", fields: { planScope: data.planScope, routeCdNm: data.routeCd } };
    case "route":
      return { reduxKey: "FP_ROUTE", fields: { planScope: data.planScope, routeCdNm: data.routeCd } };
    case "inventory":
      // 품목 화면의 인벤토리 탭(index 1)으로 전환하면서 인벤토리 코드를 채운다(Item.js TAB_SEARCH_FIELDS).
      return { reduxKey: "FP_ITEM", fields: { planScope: data.planScope, inventoryCdNm: data.inventoryCd, tabIndex: 1 } };
    default:
      return null;
  }
};

/** old connectBor — borSetCd가 있으면 BOR셋 노드 아래에 자원/공구를, 없으면 자원을 공정에 직접 붙인다. */
const connectBor = (targetNode, data, t) => {
  data.bors?.forEach((borData) => {
    if (borData.borSetCd) {
      const borSetNode = createNode(borData.borSetCd, borData.borSetCd, "borset", makeBorSetTooltip(borData, t));
      borSetNode.nav = getNavByType("borset", data);
      targetNode.children.push(borSetNode);

      borData.borSetMembers?.forEach((member) => {
        if (member.toolResourceCd) {
          const toolNode = createNode(member.toolResourceCd, member.toolResourceCd, "tool", "");
          toolNode.toolResourceCd = member.toolResourceCd;
          toolNode.usableCnt = member.usableCnt;
          toolNode.nav = getNavByType("tool", member);
          // old diagram.js:43-44 — 공구만 도형을 작게 그린다(배치 크기는 전 노드 공통).
          toolNode.width = 50;
          toolNode.height = 50;
          borSetNode.children.push(toolNode);
        } else {
          const resourceNode = createNode(member.resourceCd, member.resourceNm, "resource", makeResourceTooltip(member, t));
          resourceNode.resourceTpCd = member.resourceTpCd;
          resourceNode.altResourcePriority = member.altResourcePriority;
          resourceNode.nav = getNavByType("resource", member);
          borSetNode.children.push(resourceNode);
        }
      });
    } else {
      const resourceNode = createNode(borData.resourceCd, borData.resourceNm, "resource", makeResourceTooltip(borData, t));
      resourceNode.altResourcePriority = borData.altResourcePriority;
      resourceNode.nav = getNavByType("resource", borData);
      targetNode.children.push(resourceNode);
    }
  });
};

/** old connectByproduct — 부산물은 회색 도형 + (§변환에서) 방향이 뒤집힌 점선 엣지. */
const connectByproduct = (targetNode, data, t, itemLabelType) => {
  data.byproducts?.forEach((byproductData) => {
    const node = createNode(
      byproductData.inventoryCd, byproductData.inventoryNm, "item",
      makeInventoryTooltip(byproductData, t, true),
      itemLabelType === "inventory" ? byproductData.inventoryNm : byproductData.itemNm
    );
    node.bomRate = byproductData.bomRate;
    node.productRate = byproductData.productRate;
    markByproduct(node);
    node.nav = getNavByType("inventory", byproductData);
    node.fill = "#e1e1e1";
    node.stroke = "#424242";
    targetNode.children.push(node);
  });
};

/** old connectChildren — 자재(인벤토리)를 붙이고, 그 자재가 또 생산품이면 재귀로 공정을 붙인다. */
const connectChildren = (targetNode, data, t, includeBor, itemLabelType) => {
  data.children?.forEach((child) => {
    const inventoryNode = createNode(
      child.inventoryCd, child.inventoryNm, "item",
      makeInventoryTooltip(child, t, child.type === "byproduct"),
      itemLabelType === "inventory" ? child.inventoryNm : child.itemNm
    );
    inventoryNode.bomRate = child.bomRate;
    if (child.type === "byproduct") {
      inventoryNode.productRate = child.productRate;
      markByproduct(inventoryNode);
    }
    inventoryNode.nav = getNavByType("inventory", child);

    targetNode.children.push(inventoryNode);

    if (child.children?.length > 0) {
      connectRoute(inventoryNode, child, t, includeBor, itemLabelType);
    }
  });
};

/** old connectRoute — 인벤토리 노드 아래에 그 인벤토리를 만드는 공정(+BOR/부산물/자재)을 붙인다. */
export const connectRoute = (targetNode, data, t, includeBor = true, itemLabelType = "inventory") => {
  const routeNode = createNode(data.routeCd, data.routeNm, "route", makeRouteTooltip(data, t));
  routeNode.productRate = data.productRate;
  routeNode.nav = getNavByType("route", data);

  if (includeBor) connectBor(routeNode, data, t);
  connectByproduct(routeNode, data, t, itemLabelType);
  connectChildren(routeNode, data, t, includeBor, itemLabelType);

  targetNode.children.push(routeNode);
};

/**
 * 백엔드 `getFlo`는 선택 인벤토리에 걸린 **공정 하나당 맵 하나**를 배열로 돌려준다(`tops` 루프).
 * 즉 응답 배열의 원소들은 "같은 품목의 서로 다른 공정"이다 — 예전엔 화면이 `res.data[0]`만 써서
 * 루트의 대체 공정을 통째로 버렸다(old도 동일, 2026-08-14 수정).
 */
const normalizeRoots = (data) =>
  (Array.isArray(data) ? data : [data]).filter((d) => d && Object.keys(d).length > 0);

/**
 * old `Flo.jsx:209-217` makeFloDiagramData — 최종 인벤토리를 루트로 트리 전체를 만든다.
 * 응답 맵마다 **자기 루트 노드 객체**를 만든다. id가 같아 §변환에서 도형 하나로 병합되지만,
 * 엣지 라벨은 병합 전 원본 노드에서 계산되므로 **공정별 소요량이 각자 유지된다.**
 */
export const buildFloTree = (data, t) => normalizeRoots(data).map((root) => {
  const rootNode = createNode(root.inventoryCd, root.inventoryNm, "item", makeInventoryTooltip(root, t));
  rootNode.nav = getNavByType("inventory", root);
  connectRoute(rootNode, root, t);
  return rootNode;
});

/**
 * old `ByproductDiagram.jsx:213-228` connectConsumptionLinks — 공정이 소비하는 자재(인벤토리)를
 * 공정 노드에 붙인다. FLO의 `connectChildren`과 달리 **재귀 전개가 없다**(1단계만) —
 * 백엔드 `MasterService.getRouteFlo`가 `children`이 아니라 평평한 `consumptions` 배열을 주기 때문.
 */
const connectConsumptions = (targetNode, data, t) => {
  data.consumptions?.forEach((consumption) => {
    const node = createNode(consumption.inventoryCd, consumption.inventoryNm, "item", makeInventoryTooltip(consumption, t));
    node.bomRate = consumption.bomRate;
    // old ByproductDiagram.jsx:220-223 — 소비 자재 중에도 부산물이 섞여 올 수 있다(엣지 방향 반전 대상).
    if (consumption.type === "byproduct") {
      node.productRate = consumption.productRate;
      node.productType = "byproduct";
    }
    node.nav = getNavByType("inventory", consumption);
    targetNode.children.push(node);
  });
};

/**
 * old `ByproductDiagram.jsx:171-193` makeFloDiagramData — 연산품 화면 하단 다이어그램용 트리.
 * 공정 1개를 기준으로 "생산 인벤토리 ← 공정 → (부산물 + 소비 자재)"만 그린다.
 * FLO(`buildFloTree`)와 다른 점:
 *   - **BOR/자원/공구를 붙이지 않는다**(old도 `connectBor` 호출이 주석 처리돼 있었다)
 *   - 자재를 `children` 재귀가 아니라 `consumptions` 1단계로만 붙인다
 */
export const buildByproductTree = (data, t) => {
  const rootNode = createNode(data.inventoryCd, data.inventoryNm, "item", makeInventoryTooltip(data, t));
  rootNode.nav = getNavByType("inventory", data);

  const routeNode = createNode(data.routeCd, data.routeNm, "route", makeRouteTooltip(data, t));
  routeNode.productRate = data.productRate;
  routeNode.nav = getNavByType("route", data);

  connectByproduct(routeNode, data, t, "inventory");
  connectConsumptions(routeNode, data, t);

  rootNode.children.push(routeNode);
  return rootNode;
};

/**
 * old `BomDiagram.jsx:173-183` makeFloDiagramData — BOM 다이어그램용 트리.
 * FLO(`buildFloTree`)와 두 가지가 다르다:
 *   - 노드 라벨이 인벤토리 명칭이 아니라 **품목 명칭**(`itemLabelType: "item"`, 루트도 `data.itemNm`)
 *   - **BOR/자원/공구를 붙이지 않는다**(`includeBor: false`) — BOM 구조만 본다
 * 루트의 bomRate/productRate도 old와 동일하게 채운다(엣지 라벨 계산에 쓰인다).
 */
export const buildBomTree = (data, t) => normalizeRoots(data).map((root) => {
  const rootNode = createNode(root.inventoryCd, root.inventoryNm, "item", makeInventoryTooltip(root, t), root.itemNm);
  rootNode.nav = getNavByType("inventory", root);
  rootNode.bomRate = root.bomRate;
  rootNode.productRate = root.productRate;
  connectRoute(rootNode, root, t, false, "item");
  return rootNode;
});

/* ------------------------------------------------------------------ *
 * §변환 — 트리 → reactflow 노드/엣지
 * ------------------------------------------------------------------ */

/**
 * old `TidyTreeUtil.setTreeData`(d3.hierarchy + sort + descendants/links) 대응.
 * d3.tree()가 계산하던 좌표는 old에서도 dagre가 전부 덮어써 폐기됐으므로(조사 결론),
 * 여기서는 좌표 계산 없이 "라벨 오름차순 정렬 + 전위순회 평탄화 + 부모-자식 링크 추출"만 한다.
 */
const flattenTree = (root) => {
  const nodes = [];
  const links = [];

  const visit = (node) => {
    nodes.push(node);
    const children = [...(node.children ?? [])].sort((a, b) => String(a.label).localeCompare(String(b.label)));
    children.forEach((child) => {
      links.push({ source: child, target: node });   // old hierarchyType "CTOP" — 자식 → 부모
      visit(child);
    });
  };

  visit(root);
  return { nodes, links };
};

/**
 * old `Flo.jsx:126-191` 엣지 라벨 규칙 이식.
 * 소스/타겟 도형 종류에 따라 소요량(bomRate) / 생산비율(productRate) / 우선순위 / 주 자원 /
 * 사용 가능 수량을 라벨로 붙인다. old에 없던 방어 코드(undefined 체크)만 추가했다.
 */
const resolveEdgeLabel = (sourceNode, targetNode, t) => {
  const sourceType = sourceNode.shape;
  const targetType = targetNode.shape;

  // 자원/BOR셋이 2개 이상일 때만 우선순위를 표기한다(old와 동일 판정).
  const hasAlternatives = (node) =>
    (node.children ?? []).filter((m) => m.shape === "resource" || m.shape === "borset").length !== 1;

  if (sourceType === "item") return numberUtil.formatNumber(sourceNode.bomRate);
  if (sourceType === "route") return numberUtil.formatNumber(sourceNode.productRate);

  if (sourceType === "borset" && hasAlternatives(targetNode)) {
    const borSetPriority = (sourceNode.children ?? [])
      .reduce((sum, member) => sum + (member.altResourcePriority ?? 0), 0);
    return `${t("FP_PRIORITY")} ${numberUtil.formatNumber(borSetPriority)}`;
  }

  if (sourceType === "resource") {
    if (targetType === "route" && hasAlternatives(targetNode)) {
      const self = (targetNode.children ?? []).find((member) => member.id === sourceNode.id);
      if (self) return `${t("FP_PRIORITY")} ${numberUtil.formatNumber(self.altResourcePriority)}`;
    } else if (targetType === "borset") {
      const mainResNode = (targetNode.children ?? []).find((child) => child.resourceTpCd === "M");
      if (mainResNode && mainResNode.id === sourceNode.id) return t("FP_RESOURCE_TP_CD");
    }
  }

  if (sourceType === "tool") {
    const toolNode = (targetNode.children ?? []).find((member) => member.toolResourceCd && member.id === sourceNode.id);
    if (toolNode) return `${t("FP_TOOL_USABLE_CNT")} : ${numberUtil.formatNumber(toolNode.usableCnt)}`;
  }

  return "";
};

const BYPRODUCT_EDGE_STYLE = { stroke: "#333", strokeWidth: 1, strokeDasharray: "4 2" };
const DEFAULT_EDGE_STYLE = { stroke: "#000", strokeWidth: 1 };

/* ------------------------------------------------------------------ *
 * §배치 — dagre 대체 계층 레이아웃 (좌표만 계산)
 * ------------------------------------------------------------------ */

/** DFS로 back edge(사이클 유발 간선)를 찾아낸다. 랭킹에서만 제외하고 렌더링에는 그대로 남긴다. */
const findBackEdges = (nodeIds, outgoing) => {
  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map(nodeIds.map((id) => [id, WHITE]));
  const backEdges = new Set();

  const visit = (id) => {
    color.set(id, GRAY);
    (outgoing.get(id) ?? []).forEach((edge) => {
      const next = edge.target;
      if (color.get(next) === GRAY) backEdges.add(edge.id);
      else if (color.get(next) === WHITE) visit(next);
    });
    color.set(id, BLACK);
  };

  nodeIds.forEach((id) => { if (color.get(id) === WHITE) visit(id); });
  return backEdges;
};

/** longest-path 랭킹 — rank(target) = max(rank(source)) + 1. 위상정렬 순으로 한 번에 계산. */
const assignRanks = (nodeIds, edges, incoming, outgoing) => {
  const inDegree = new Map(nodeIds.map((id) => [id, (incoming.get(id) ?? []).length]));
  const rank = new Map(nodeIds.map((id) => [id, 0]));

  const queue = nodeIds.filter((id) => inDegree.get(id) === 0);
  const ordered = [];
  while (queue.length > 0) {
    const id = queue.shift();
    ordered.push(id);
    (outgoing.get(id) ?? []).forEach((edge) => {
      const next = edge.target;
      rank.set(next, Math.max(rank.get(next), rank.get(id) + 1));
      inDegree.set(next, inDegree.get(next) - 1);
      if (inDegree.get(next) === 0) queue.push(next);
    });
  }

  // 사이클 제거 후에도 위상정렬에 안 잡힌 노드가 남으면(방어) 랭크 0으로 둔다.
  nodeIds.forEach((id) => { if (!ordered.includes(id)) rank.set(id, rank.get(id) ?? 0); });

  /**
   * **오른쪽 당김(정렬) 1패스** — longest-path만 쓰면 모든 노드가 "소스로부터의 거리"로 정해져
   * **왼쪽 정렬**된다. 그래서 길이가 다른 두 경로가 한 노드로 합류할 때(예: 한쪽은 첫 공정부터
   * 생산, 다른 쪽은 재고로 시작해 두 번째 공정부터) **같은 공정이 다른 열에 놓이고**, 짧은 쪽이
   * 끝에서 여러 칸을 건너뛰는 긴 엣지로 합류한다(2026-08-14 실측).
   *
   * rank 내림차순으로 훑으며 `rank = min(후행자 rank) - 1`로 당기면 **끝이 정렬되고 같은 공정이
   * 같은 열에 온다.** longest-path 결과는 항상 `rank <= min(후행자)-1`이라 이 이동은 오른쪽
   * 방향뿐이고, 선행자 제약(`선행자 < 자신`)도 계속 만족한다. 내림차순이라 후행자가 먼저
   * 확정되므로 전파도 1패스로 끝난다.
   *
   * ⚠️ **후행자가 없는 노드는 건드리지 않는다.** 연산품(공정 → 연산품)처럼 곁가지로 끝나는
   * 노드까지 당기면 맨 오른쪽 끝으로 날아가 자기 공정에서 멀어진다.
   * (자원/공구를 소비 공정 바로 왼쪽으로 붙이던 옛 보정도 이 규칙에 흡수됐다 — 별도 처리 불필요.)
   */
  nodeIds.slice()
    .sort((a, b) => rank.get(b) - rank.get(a))
    .forEach((id) => {
      const successors = outgoing.get(id) ?? [];
      if (successors.length === 0) return;
      const tightest = Math.min(...successors.map((edge) => rank.get(edge.target)));
      rank.set(id, Math.max(0, tightest - 1));
    });

  return rank;
};

/**
 * 랭크 내 y 좌표 — **가지마다 자기 가로선(track)** 방식.
 *
 * 이 도메인의 그래프는 일반 DAG가 아니라 "긴 직선 척추(자재→공정→자재→공정…)에 자원·재고자재가
 * 소수로 매달린" 형태다. 그래서 dagre식 균형 배치(모든 노드를 이웃 중앙으로 당김)보다,
 * **주 경로를 곧게 깔고 갈라지는 가지를 통째로 다른 가로선에 올리는** 쪽이 훨씬 읽기 쉽다
 * (2026-08-13 사용자 확인 — 공정이 길어질수록 차이가 커진다).
 *
 * 절차:
 *   1. 싱크(최종 산출물)에서 시작해 **주 선행자**(= 가장 긴 가지, 동률이면 자원보다 자재/공정)를
 *      따라가며 같은 y로 곧게 깐다.
 *   2. 도중에 만난 나머지 선행자 중 **잎**(자원처럼 선행자가 없는 노드)은 그 자리에서 바로 옆
 *      빈 선에 붙인다 — 긴 가지가 근처 선을 먼저 차지해 자원이 멀리 밀리는 것을 막는다.
 *   3. 잎이 아닌 가지는 체인 통째로 새 가로선에 올린다. 그 체인이 지나는 **모든 랭크에서 비어
 *      있는** 가장 가까운 선을 고르므로 겹치지도, 서로 넘나들지도 않는다.
 *
 * 이전 구현(랭크별 median + 겹침 해소)의 문제: 마지막 패스가 한 방향만 보게 돼 있어 체인 전체가
 * 한 줄로 붕괴하고 나머지가 아래로 밀렸으며, 공정이 길어지면 계단식으로 흘러 세로 폭이 공정 수에
 * 비례해 커졌다(10공정 기준 960px → 이 방식은 120px).
 */
const assignTrackCoordinates = (nodeIds, byId, rank, incoming, outgoing) => {
  // 소스로부터의 최장 거리 — 가장 긴 가지를 척추로 삼기 위한 값.
  const height = new Map();
  const heightOf = (id) => {
    if (height.has(id)) return height.get(id);
    height.set(id, 0);   // 사이클 방어(재진입 시 0으로 취급)
    const preds = incoming.get(id) ?? [];
    const h = preds.length === 0 ? 0 : 1 + Math.max(...preds.map((e) => heightOf(e.source)));
    height.set(id, h);
    return h;
  };
  nodeIds.forEach(heightOf);

  const isAux = (id) => {
    const shape = byId.get(id)?.data?.shape;
    return shape === "resource" || shape === "tool" || shape === "borset";
  };
  const predsOf = (id) => (incoming.get(id) ?? []).map((e) => e.source)
    .sort((a, b) => (heightOf(b) - heightOf(a))
      || (isAux(a) - isAux(b))
      || String(byId.get(a)?.data?.label ?? a).localeCompare(String(byId.get(b)?.data?.label ?? b)));

  const y = new Map();
  const occupied = new Map();   // rank -> Set(y)
  const take = (id, value) => {
    y.set(id, value);
    const r = rank.get(id);
    if (!occupied.has(r)) occupied.set(r, new Set());
    occupied.get(r).add(value);
  };

  // id에서 주 선행자만 따라간 체인(= 같은 가로선에 올라갈 노드들)이 차지할 랭크 목록.
  const chainRanks = (id) => {
    const ranks = [];
    const seen = new Set();
    let cur = id;
    while (cur && !y.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      ranks.push(rank.get(cur));
      cur = predsOf(cur)[0];
    }
    return ranks;
  };

  const isFree = (value, ranks) => ranks.every((r) => !occupied.get(r)?.has(value));

  /**
   * base에서 가장 가까운 **연속된** 빈 가로선 묶음: 아래 k칸, 위 k칸을 번갈아 보며
   * 구성원 전원이 한 방향으로 나란히 들어갈 자리를 찾는다(k=1,2,3…).
   * 묶음으로 잡는 이유: 한 공정에 붙는 **같은 종류 입력(대체자원 여러 개 등)이 흩어지지 않게**
   * 하기 위함이다(2026-08-13 사용자 요청 — 품목이 자원들 사이에 끼어 보였다).
   * 구성원이 1개면 기존 "가장 가까운 빈 선 하나"와 동일하게 동작한다.
   */
  const findTrackRun = (base, memberRanks) => {
    for (let k = 1; k <= nodeIds.length + 1; k += 1) {
      for (const dir of [1, -1]) {
        const tracks = memberRanks.map((_, i) => base + dir * (k + i) * NODE_GAP);
        if (memberRanks.every((ranks, i) => isFree(tracks[i], ranks))) return tracks;
      }
    }
    return memberRanks.map((_, i) => base + (i + 1) * NODE_GAP);
  };

  // 가로선을 잡는 순서 = 도형 종류(자재 → BOR셋 → 자원 → 공구) → 긴 가지 → 라벨.
  // 같은 종류가 연달아 오므로 아래 groupByCategory가 그대로 연속 블록이 된다.
  const CATEGORY_ORDER = { item: 0, order: 0, borset: 1, resource: 2, tool: 3 };
  const categoryOf = (id) => CATEGORY_ORDER[byId.get(id)?.data?.shape] ?? 0;
  const groupByCategory = (ids) => {
    const sorted = ids.slice().sort((a, b) => (categoryOf(a) - categoryOf(b))
      || (heightOf(b) - heightOf(a))
      || String(byId.get(a)?.data?.label ?? a).localeCompare(String(byId.get(b)?.data?.label ?? b)));
    const groups = [];
    sorted.forEach((id) => {
      const last = groups[groups.length - 1];
      if (last && categoryOf(last[0]) === categoryOf(id)) last.push(id);
      else groups.push([id]);
    });
    return groups;
  };

  const reservedTrack = new Map();   // 가지 머리 id -> 확정된 가로선

  // 체인을 base에 곧게 깔고, 도중에 만난 "잎이 아닌" 선행자들을 다음 가지 후보로 돌려준다.
  /**
   * "자기 상류 체인을 아직 안 깐 가지" = 큰 가지. 잎(자원/공구/재고/연산품처럼 자기 선행자가
   * 없거나 이미 다 놓인 것)과 구분한다. 연산품은 선행자(공정)가 **이미 놓인 상태**라 잎이 된다.
   */
  const isMajorBranch = (id) => predsOf(id).some((p) => !y.has(p));

  const layChain = (startId, base) => {
    const branches = [];
    // 큰 가지는 **척추와 그 잎이 전부 놓인 뒤에** 자리를 잡는다(아래 § 참고).
    const deferred = [];
    let cur = startId;
    while (cur && !y.has(cur)) {
      // 다른 가로선으로 이미 예약된 가지를 만나면 여기서 멈춘다(그 가지는 자기 차례에 깔린다).
      const reserved = reservedTrack.get(cur);
      if (reserved !== undefined && reserved !== base) break;

      take(cur, base);
      const [main, ...rest] = predsOf(cur);

      // 연산품처럼 **후행자로 붙는 곁가지**(공정 → 연산품)는 선행자 목록에 안 잡힌다. 자기 후행자가
      // 없는 미배치 노드만 골라 여기서 같이 배치한다 — 안 그러면 아래 sinks 루프에서 "새 루트"로
      // 분류돼 **base 0 기준으로 따로** 자리를 잡고, 같은 공정의 연산품 둘이 위/아래 정반대 끝으로
      // 흩어진다(2026-08-14 실측: 한 공정의 연산품이 y=0과 y=600으로 갈라짐).
      const sideOut = (outgoing.get(cur) ?? [])
        .map((edge) => edge.target)
        .filter((id) => (outgoing.get(id) ?? []).length === 0);

      const pending = [...new Set([...rest, ...sideOut])]
        .filter((id) => !y.has(id) && !reservedTrack.has(id));

      deferred.push(...pending.filter(isMajorBranch));

      // 잎은 자기를 소비하는 노드 바로 옆에 붙인다(= 이 체인의 base 기준).
      groupByCategory(pending.filter((id) => !isMajorBranch(id))).forEach((group) => {
        const tracks = findTrackRun(base, group.map((id) => [rank.get(id)]));
        group.forEach((id, i) => take(id, tracks[i]));
      });

      cur = main;
    }

    /*
     * § 큰 가지는 **척추를 다 깔고 잎까지 붙인 다음에** 자리를 준다.
     *
     * 예전엔 만나는 즉시 예약했는데, 그러면 척추 바로 아래(base+120)를 큰 가지가 먼저 차지하고
     * **척추의 잎들이 갈 곳이 없어 그 큰 가지 아래로 흩어져 들어간다**. 주문 추적에서 로트 A의
     * 연산품·자재가 로트 B의 경로 사이사이에 끼어 보이던 원인이 이것이다(2026-08-14 실측:
     * 로트 A 연산품이 로트 B 척추 아래에, 로트 B의 재고 2건 사이에 끼었다).
     *
     * 뒤로 미루면 잎이 척추에 먼저 붙고, 큰 가지는 그 아래 **빈 띠(band)**를 통째로 잡는다 —
     * 로트/서브트리마다 자기 가로 띠를 갖게 되어 경로가 서로 섞이지 않는다.
     */
    groupByCategory(deferred.filter((id) => !y.has(id) && !reservedTrack.has(id))).forEach((group) => {
      const memberRanks = group.map((id) => chainRanks(id));
      const tracks = findTrackRun(base, memberRanks);
      group.forEach((id, i) => {
        // 가지는 나중에 펼치되, 그 체인이 지날 랭크를 지금 예약해 다른 노드가 끼어들지 못하게 한다.
        memberRanks[i].forEach((r) => {
          if (!occupied.has(r)) occupied.set(r, new Set());
          occupied.get(r).add(tracks[i]);
        });
        reservedTrack.set(id, tracks[i]);
        branches.push(id);
      });
    });

    return branches;
  };

  const runFrom = (startId, base) => {
    const queue = layChain(startId, base);
    while (queue.length > 0) {
      const id = queue.shift();
      if (y.has(id)) continue;
      // 가로선은 이 가지를 만들 때 이미 확정·예약해 뒀다(layChain의 reservedTrack).
      queue.push(...layChain(id, reservedTrack.get(id) ?? base));
    }
  };

  // 새 루트(싱크)를 놓을 빈 가로선 — 구성원 1개짜리 묶음이라 "가장 가까운 빈 선"과 같다.
  const freeTrackFor = (id) => (y.size === 0 ? 0 : findTrackRun(0, [chainRanks(id)])[0]);

  // 싱크(후행자가 없는 노드)부터. 여러 개면 긴 것부터, 남은 노드가 있으면 새 가로선에서 다시.
  const sinks = nodeIds.filter((id) => (outgoing.get(id) ?? []).length === 0)
    .sort((a, b) => heightOf(b) - heightOf(a));
  sinks.forEach((id) => { if (!y.has(id)) runFrom(id, freeTrackFor(id)); });

  // 싱크에서 역방향으로 닿지 않는 노드(사이클 제거로 고립된 경우 등)까지 빠짐없이 배치한다.
  nodeIds.slice().sort((a, b) => heightOf(b) - heightOf(a))
    .forEach((id) => { if (!y.has(id)) runFrom(id, freeTrackFor(id)); });

  // 전체를 0 기준으로 올린다(음수 좌표 방지).
  const minY = Math.min(...[...y.values()]);
  y.forEach((value, id) => y.set(id, value - minY));
  return y;
};

/**
 * dagre 대체 레이아웃. 노드에 `position:{x,y}`를 채워 돌려준다(엣지는 그대로 통과 —
 * 엣지 경로는 reactflow가 그린다).
 * 노드 크기는 old와 동일하게 전 노드 공통 상수(NODE_WIDTH/NODE_HEIGHT)로 취급한다.
 */
export const layoutFlow = (nodes, edges) => {
  if (nodes.length === 0) return { nodes, edges };

  const nodeIds = nodes.map((n) => n.id);
  const known = new Set(nodeIds);
  const validEdges = edges.filter((e) => known.has(e.source) && known.has(e.target) && e.source !== e.target);

  const outgoing = new Map(nodeIds.map((id) => [id, []]));
  const incoming = new Map(nodeIds.map((id) => [id, []]));
  validEdges.forEach((edge) => {
    outgoing.get(edge.source).push(edge);
    incoming.get(edge.target).push(edge);
  });

  // 1) 사이클 제거 — old는 dagre 내부 acyclic 전처리에 의존하고 있었다(부산물 엣지 방향 반전 +
  //    같은 코드 노드 병합으로 사이클이 생길 수 있음). 그 역할을 여기서 대신한다.
  const backEdges = findBackEdges(nodeIds, outgoing);
  if (backEdges.size > 0) {
    nodeIds.forEach((id) => {
      outgoing.set(id, outgoing.get(id).filter((e) => !backEdges.has(e.id)));
      incoming.set(id, incoming.get(id).filter((e) => !backEdges.has(e.id)));
    });
  }

  // 2) 랭킹(x) → 3) 가로선 배정(y)
  const rank = assignRanks(nodeIds, validEdges, incoming, outgoing);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const y = assignTrackCoordinates(nodeIds, byId, rank, incoming, outgoing);

  const positioned = nodes.map((node) => ({
    ...node,
    position: { x: rank.get(node.id) * RANK_GAP, y: y.get(node.id) ?? 0 }
  }));

  return { nodes: positioned, edges };
};

/* ------------------------------------------------------------------ *
 * §진입점 — 백엔드 응답 1건 → reactflow {nodes, edges}
 * ------------------------------------------------------------------ */

/**
 * 화면용 트리 → reactflow {nodes, edges}. 트리 조립만 화면별로 다르고(§조립) 이 변환 단계는
 * 전 다이어그램 화면이 공유한다.
 *
 * @param {object} root         `buildFloTree`/`buildBomTree`/`buildOrderTrackingTree`가 만든 트리 루트
 * @param {function} t          i18n 번역 함수
 * @param {boolean} backward    역전개(old hierarchyType "PTOC") 여부. false면 정전개("CTOP").
 * @param {function} [edgeLabel] 엣지 라벨 계산을 화면별로 갈아끼울 때. `(source, target, isByproduct) => string`.
 *                              생략하면 BOM 계열 규칙(`resolveEdgeLabel` + 부산물 productRate)을 쓴다.
 *                              주문 추적은 노드가 들고 있는 `edgeLabel` 문자열을 그대로 쓰므로 이걸 넘긴다.
 * @param {boolean} [animated]  엣지 애니메이션(old OrderTrackingDiagram만 `animated: true`였다).
 */
const treeToFlow = (roots, t, { backward = false, edgeLabel, animated = false } = {}) => {
  // 루트가 여러 개면(= 선택 품목에 걸린 공정이 여럿) 각 트리를 평탄화해 합친다.
  // 같은 id의 루트 노드는 아래 병합 단계에서 도형 하나가 되고, 공정만 N개로 갈라진다.
  const flattened = (Array.isArray(roots) ? roots : [roots]).map(flattenTree);
  const treeNodes = flattened.flatMap((f) => f.nodes);
  const links = flattened.flatMap((f) => f.links);

  // old `TidyTreeUtil.makeEdges(plinks, hierarchyType, ...)` — 정전개는 자식→부모(CTOP),
  // 역전개는 부모→자식(PTOC)으로 엣지 방향이 통째로 반대다(old BomDiagram.jsx:65).
  // flattenTree가 이미 CTOP(자식→부모)으로 주므로 역전개일 때만 뒤집는다.
  const baseLinks = backward
    ? links.map((link) => ({ source: link.target, target: link.source }))
    : links;

  // old Flo.jsx:83-90 / BomDiagram.jsx:56-63 — 부산물은 공정에서 "나오는" 것이라 엣지 방향을
  // 뒤집는다. 판정 대상은 **기본 방향에서 source 쪽에 온 노드**다(정전개=트리의 자식,
  // 역전개=트리의 부모 — old가 direction에 따라 link.target/link.source를 나눠 검사한 것이
  // 기본 방향까지 감안하면 이 한 줄과 같다).
  const directedLinks = baseLinks.map((link) =>
    link.source.productType === "byproduct" ? { source: link.target, target: link.source } : link
  );

  // 같은 업무코드는 1개 노드로 병합(old uniqueByCondition). 트리에서 여러 번 등장한 공정/자원/
  // 공구가 하나로 합쳐지면서 부모가 여럿인 DAG가 된다 — 그래서 트리 레이아웃으로는 배치할 수 없다.
  const nodeById = new Map();
  treeNodes.forEach((node) => { if (!nodeById.has(node.id)) nodeById.set(node.id, node); });

  const edgeById = new Map();
  directedLinks.forEach((link) => {
    const id = `${link.source.id}${link.target.id}`;
    if (edgeById.has(id) || link.source.id === link.target.id) return;
    edgeById.set(id, { id, source: link.source, target: link.target });
  });

  const flowNodes = [...nodeById.values()].map((node) => ({
    id: node.id,
    type: "floNode",
    position: { x: 0, y: 0 },
    data: {
      label: node.label,
      shape: node.shape,
      tooltip: node.tooltip,
      nav: node.nav,
      fill: node.fill,
      stroke: node.stroke,
      width: node.width,
      height: node.height,
      // old FCFLOWidget의 problemStatus — 값이 있으면 도형을 흰 배경 + 굵은 빨간 테두리로 강조한다
      // (주문 추적의 납기 지연 작업주문). FlowNode.js가 처리.
      problemStatus: node.problemStatus
    }
  }));

  const flowEdges = [...edgeById.values()].map(({ id, source, target }) => {
    const isByproduct = target.productType === "byproduct";
    return {
      id,
      source: source.id,
      target: target.id,
      sourceHandle: `${source.id}_right`,
      targetHandle: `${target.id}_left`,
      type: "default",
      animated,
      // old는 부산물 엣지만 productRate 라벨 + 점선으로 덮어썼다(Flo.jsx:183-190).
      label: edgeLabel
        ? edgeLabel(source, target, isByproduct)
        : (isByproduct ? numberUtil.formatNumber(target.productRate) : resolveEdgeLabel(source, target, t)),
      labelStyle: { fill: "#000", fontSize: 10 },
      style: isByproduct ? BYPRODUCT_EDGE_STYLE : DEFAULT_EDGE_STYLE,
      markerEnd: { type: "arrowclosed", color: "#000" }
    };
  });

  return layoutFlow(flowNodes, flowEdges);
};

/**
 * old `Flo.jsx`의 makeFloDiagramData + setFloDiagram 전체 파이프라인 대응.
 *
 * @param {object} data   백엔드 `factoryplan/master/bom/flo` 응답의 단일 트리(res.data[0])
 * @param {function} t    i18n 번역 함수
 * @returns {{nodes: Array, edges: Array}} reactflow용 노드/엣지
 */
export const floDataToFlow = (data, t) => {
  const roots = buildFloTree(data, t);
  if (roots.length === 0) return { nodes: [], edges: [] };
  return treeToFlow(roots, t);
};

/**
 * old `BomDiagram.jsx`의 makeFloDiagramData + setFloDiagram 파이프라인 대응.
 *
 * @param {object} data        백엔드 `factoryplan/master/bom/bom-diagram(/reverse)` 응답의 단일 트리
 * @param {function} t         i18n 번역 함수
 * @param {string} direction   "forward"(정전개) | "backward"(역전개)
 */
export const bomDataToFlow = (data, t, direction = "forward") => {
  const roots = buildBomTree(data, t);
  if (roots.length === 0) return { nodes: [], edges: [] };
  return treeToFlow(roots, t, { backward: direction === "backward" });
};

/**
 * old `ByproductDiagram.jsx`의 makeFloDiagramData + setFloDiagram 파이프라인 대응.
 * 부산물 엣지(방향 반전 + 점선 + productRate 라벨) 처리는 FLO와 완전히 동일해서
 * `treeToFlow` 기본 규칙을 그대로 쓴다(별도 `edgeLabel` 불필요).
 *
 * @param {object} data   백엔드 `factoryplan/master/byproduct/byproducts/route-flo` 응답의 단일 맵(res.data[0])
 * @param {function} t    i18n 번역 함수
 */
export const byproductFloToFlow = (data, t) => {
  if (!data || Object.keys(data).length === 0) return { nodes: [], edges: [] };
  return treeToFlow(buildByproductTree(data, t), t);
};

/* ------------------------------------------------------------------ *
 * §주문 추적 — old `analysis/ordertracking/OrderTrackingDiagram.jsx` 이식
 *
 * BOM 계열(FLO/BomDiagram)과 달리 old에서도 `diagram.js`를 쓰지 않고 자체 변환 로직을 갖고
 * 있던 화면이라, 조립 규칙이 통째로 다르다:
 *   - 루트가 인벤토리가 아니라 **작업주문**(order 도형), 납기 지연이면 problemStatus로 강조
 *   - 자재/공정/자원을 데이터의 재귀 구조(`rows`)를 타고 붙인다(BOM rate 개념 없음)
 *   - 엣지 라벨이 계산식이 아니라 **노드가 들고 있는 문자열**(`수량 (일시)`)이다
 * 공통인 §변환/§배치(treeToFlow/layoutFlow)는 그대로 재사용한다.
 * ------------------------------------------------------------------ */

/**
 * 일시 표시 — **그리드와 완전히 같은 규칙**을 쓴다.
 *
 * 백엔드가 주는 값은 `2026-08-12T20:30:00Z`(UTC)이고, 그리드 컬럼은 `dateTimeColumn`
 * (= `dateUtils.formatDateTime`)으로 **로컬 타임존 변환 후 `yyyy-MM-dd HH:mm:ss`** 로 찍는다.
 * old는 다이어그램에서 문자열의 `T`만 공백으로 바꿔 그대로 보여줬는데(`convertToDatetime`),
 * 그러면 같은 행인데 위 그리드와 아래 다이어그램의 시각이 달라 보인다(KST 기준 9시간 차이).
 * 툴팁과 엣지 라벨이 모두 이 함수를 거치므로 여기 한 곳만 그리드와 맞추면 된다.
 */
const toDateTimeText = (value) => dateUtils.formatDateTime(value);
const toQtyText = (value) => (value === null || value === undefined ? "" : numberUtil.formatNumber(value));

const makeWorkOrderTooltip = (data, t) => [
  `${t("FP_REQUEST_QTY")} : ${toQtyText(data.qty)}`,
  `${t("FP_DUE_DT")} : ${toDateTimeText(data.dueDt)}`,
  `${t("FP_START_TS")} : ${toDateTimeText(data.startTs)}`,
  `${t("FP_END_TS")} : ${toDateTimeText(data.endTs)}`
].join("\n");

// old PROD_QTY / STOCK_CD — new에 `PROD_QTY` 키가 없어 같은 의미의 기존 키를 재사용한다(§0-3).
// `stockCd`는 isStock일 때만 붙이되, 값이 없으면 `tooltipLines`가 그 줄을 통째로 뺀다
// (old는 연산품에도 isStock=true로 넘겨 `재고 코드 : undefined`가 그대로 노출됐다).
const makeTrackingItemTooltip = (data, t, isStock = false) => tooltipLines([
  [t("FP_PRODUCTION_QTY"), toQtyText(data.qty)],
  ...(isStock ? [[t("STOCK_CD"), data.stockCd]] : []),
  [t("FP_ITEM_CD"), data.itemCd],
  [t("FP_ITEM_NM"), data.itemNm],
  [t("FP_ITEM_UOM"), data.uom]
]);

const makeTrackingRouteTooltip = (data, t) => [
  `${t("FP_PRODUCTION_QTY")} : ${toQtyText(data.qty)}`,
  `${t("FP_START_TS")} : ${toDateTimeText(data.startTs)}`,
  `${t("FP_END_TS")} : ${toDateTimeText(data.endTs)}`,
  `${t("FP_ROUTE_CD")} : ${data.routeCd}`,
  `${t("FP_ROUTE_NM")} : ${data.routeNm}`
].join("\n");

const makeTrackingResourceTooltip = (data, t) =>
  `${t("FP_RESOURCE_CD")} : ${data.resourceCd}\n${t("FP_RESOURCE_NM")} : ${data.resourceNm}`;

// 재고로 충당된 자재는 회색으로 구분한다(old: fill #e1e1e1 / stroke #464646).
const STOCK_NODE_PAINT = { fill: "#e1e1e1", stroke: "#464646" };
const BYPRODUCT_NODE_PAINT = { fill: "#e1e1e1", stroke: "#424242" };

/**
 * 노드 키를 "같은 코드라도 실제로는 다른 건"까지 갈라주는 스코프 함수.
 *
 * - `@액티비티` — 한 주문이 **로트로 분할**되면(주문 수량 > 첫 공정 min lot size 등) 같은
 *   품목/공정/자원이 **서로 다른 액티비티로 두 번 이상** 등장한다. 업무코드만으로 id를 만들면
 *   §변환의 병합에서 두 로트의 경로가 한 그래프로 엉킨다(`backend-gaps.md` #10과 세트).
 * - `#재고코드` — **재고 행은 `tree`가 액티비티 id가 아니라 stageCd**다
 *   (`OrderTrackingService#mapToOrderTreeResultDto`). 그래서 같은 품목을 **여러 재고에서 나눠
 *   투입**하면(예: SP#100 10개 + SP#96 15개) 액티비티 스코프만으로는 두 행이 같은 키가 되고,
 *   소비 공정까지 같아 **엣지 id마저 겹쳐 두 번째 투입이 통째로 사라진다**(2026-08-14 실측).
 *   `stockCd`가 그 둘을 가르는 자연키다.
 *
 * 해당 값이 없으면 붙이지 않는다 — 원래 병합돼야 하는 노드(같은 재고를 두 로트가 공유하는 경우 등)는
 * 지금처럼 한 도형으로 남는다.
 */
const scopedCode = (code, { activityId, stockCd } = {}) =>
  `${code}${activityId ? `@${activityId}` : ""}${stockCd ? `#${stockCd}` : ""}`;

// old makeChildTreeDiagramData — 자재 노드를 만들고, 그 자재를 만든 공정/자원/부산물을 매단 뒤
// 하위 자재(`rows`)를 재귀로 잇는다. itemCd가 없으면 노드를 만들지 않는다(old와 동일).
const buildTrackingChild = (data, t, planScope) => {
  if (!data?.itemCd) return null;

  const isStock = data.isStock;
  const itemLabel = isStock ? `${data.itemNm} (${t("FP_STOCK")})` : data.itemNm;
  const itemNode = createNode(
    scopedCode(data.itemCd, { activityId: data.tree, stockCd: isStock ? data.stockCd : null }),
    itemLabel, "item", makeTrackingItemTooltip(data, t, isStock)
  );
  itemNode.edgeLabel = `${toQtyText(data.qty)} (${toDateTimeText(data.endTs)})`;
  itemNode.nav = { reduxKey: "FP_ITEM", fields: { planScope, itemCdNm: data.itemCd, tabIndex: 0 } };
  if (isStock) Object.assign(itemNode, STOCK_NODE_PAINT);

  let parentNode = itemNode;

  // 'Infinite Stock'은 실제 공정이 아니라 무한재고 가상 공정이라 도형을 만들지 않는다(old 조건 그대로).
  if (data.routeCd && data.routeCd !== "Infinite Stock" && data.resourceCd) {
    const routeNode = createNode(scopedCode(data.routeCd, { activityId: data.tree }), data.routeNm, "route", makeTrackingRouteTooltip(data, t));
    routeNode.edgeLabel = `${toQtyText(data.qty)} (${toDateTimeText(data.startTs)})`;
    routeNode.nav = { reduxKey: "FP_ROUTE", fields: { planScope, routeCdNm: data.routeCd } };
    itemNode.children.push(routeNode);
    parentNode = routeNode;

    const resourceNode = createNode(scopedCode(data.resourceCd, { activityId: data.tree }), data.resourceNm, "resource", makeTrackingResourceTooltip(data, t));
    // 자원 엣지에는 라벨을 붙이지 않는다 — old는 `data.qty`를 찍었지만, 바로 옆 자재/공정 엣지가
    // **같은 액티비티의 같은 값**을 이미 `수량 (일시)`로 보여준다(세 라벨이 전부 data.qty에서 나옴).
    // 구조적으로 항상 중복이고, 자원 엣지만 일시가 없어 숫자만 떠 보였다(2026-08-14 사용자 결정).
    resourceNode.nav = { reduxKey: "FP_RESOURCE", fields: { planScope, resourceCdNm: data.resourceCd } };
    routeNode.children.push(resourceNode);

    data.byproducts?.forEach((byproduct) => {
      // 연산품은 **산출한 액티비티**(data) 기준으로 스코프를 잡되, 같은 품목이 여러 재고로
      // 나뉘어 나오면 재고 코드로 한 번 더 가른다(위 자재 투입과 같은 이유).
      const byproductNode = createNode(
        scopedCode(byproduct.itemCd, { activityId: data.tree, stockCd: byproduct.stockCd }),
        byproduct.itemNm, "item", makeTrackingItemTooltip(byproduct, t, true)
      );
      byproductNode.edgeLabel = `${toQtyText(byproduct.qty)} (${toDateTimeText(byproduct.startTs)})`;
      byproductNode.nav = { reduxKey: "FP_ITEM", fields: { planScope, itemCdNm: byproduct.itemCd, tabIndex: 0 } };
      markByproduct(byproductNode);
      Object.assign(byproductNode, BYPRODUCT_NODE_PAINT);
      routeNode.children.push(byproductNode);
    });
  }

  data.rows?.forEach((child) => {
    const childNode = buildTrackingChild(child, t, planScope);
    if (childNode) parentNode.children.push(childNode);
  });

  return itemNode;
};

/** old `OrderTrackingDiagram.jsx:93-109` makeWorkOrderDiagramData — 작업주문 1건을 루트로 트리를 만든다. */
export const buildOrderTrackingTree = (data, t, planScope) => {
  const workOrderNode = createNode(data.mainCol, data.mainCol, "order", makeWorkOrderTooltip(data, t));

  // 납기(dueDt)보다 종료(endTs)가 늦으면 지연 — 도형을 빨갛게 강조한다(그리드의 지연 판정과 동일 기준).
  if (data.dueDt && data.endTs && data.dueDt < data.endTs) {
    workOrderNode.problemStatus = "LATE";
  }

  data.rows?.forEach((child) => {
    const childNode = buildTrackingChild(child, t, planScope);
    if (childNode) workOrderNode.children.push(childNode);
  });

  return workOrderNode;
};

/**
 * old `OrderTrackingDiagram.jsx`의 makeWorkOrderDiagramData + setOrderTrackingDiagram 대응.
 *
 * @param {object} data        선택한 작업주문 1건(그리드가 물고 있는 원본 트리 노드)
 * @param {function} t         i18n 번역 함수
 * @param {string} planScope   딥링크로 넘길 계획 범위
 */
export const orderTrackingDataToFlow = (data, t, planScope) => {
  if (!data || Object.keys(data).length === 0) return { nodes: [], edges: [] };

  return treeToFlow(buildOrderTrackingTree(data, t, planScope), t, {
    // old: `targetNode.productType === 'byproduct' ? targetNode.EDGE_LABEL : sourceNode.EDGE_LABEL`
    // (부산물은 방향이 뒤집혀 target 쪽에 오므로 그쪽 라벨을 쓴다).
    edgeLabel: (source, target, isByproduct) => (isByproduct ? target.edgeLabel : source.edgeLabel) ?? "",
    animated: true
  });
};

const flowchart = {
  NODE_WIDTH,
  NODE_HEIGHT,
  createNode,
  connectRoute,
  buildFloTree,
  buildBomTree,
  buildByproductTree,
  buildOrderTrackingTree,
  getNavByType,
  layoutFlow,
  floDataToFlow,
  bomDataToFlow,
  byproductFloToFlow,
  orderTrackingDataToFlow
};

export default flowchart;
